import { type Context, type SystemMessage, type ToolMessage, type UserMessage } from '@ag-ui/core';
import {
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';
import { v4 as uuid } from 'uuid';

import { type CreditAnalyticsParams, useTrackAgentChatPromptSent, useTrackSkillInvoked } from '@air/analytics';

import { AgentBusyError, AgentManager } from '../AgentManager';
import { type CanvasThreadListItem } from '../api/threads';
import { type EventSubscriptionManager } from '../EventSubscriptionManager';
import { useThreadManagement } from '../hooks/useThreadManagement';
import { useCanvasTokenNodeStore } from '../store/useCanvasTokenNodeStore';
import { useChatComposerNodeStore } from '../store/useChatComposerNodeStore';
import { useChatComposerStore } from '../store/useChatComposerStore';
import { useChatHistoryNodeStore } from '../store/useChatHistoryNodeStore';
import { getMediaMapNodes, useChatStore } from '../store/useChatStore';
import { getModelSelection } from '../store/useModelSelectionStore';
import { useRunErrorStore } from '../store/useRunErrorStore';
import { findGroupMembers } from '../store/utils/groupUtils';
import { mergeCanvasTokenNodes } from '../store/utils/mergeCanvasTokenNodes';
import { MEDIA_PATTERN } from '../tokens/tokenSerialization';
import { type GetAuthToken, type ProgrammaticMessageOptions, type ToolResponsePayload } from '../types/subscriptions';
import { ChatHistoryNodeSync } from '../ui/components/ChatHistoryNodeSync';
import { selectSkillInstructionsForMessage } from '../utils/selectSkillInstructionsForMessage';
import { extractSlashCommandsFromMessage } from '../utils/slashCommandInMessage';

export type AgentProviderProps = {
  agentUrl: string;
  children: ReactNode;
  firstName?: string;
  getAuthToken: GetAuthToken;
  canConsumeMoreCredits: boolean;
  resourceId: string;
  /** Called when resourceId is empty (draft mode). Must create the resource and return the real ID. */
  resolveResourceId?: () => Promise<string>;
  workspaceId: string;
  creditAnalyticsParams: CreditAnalyticsParams | null;
  onShowPlansModal?: () => void;
};

export type AgentContextValue = {
  currentThreadId: string | null;
  eventSubscriptions: EventSubscriptionManager | null;
  firstName?: string;
  isLoadingThreads: boolean;
  canConsumeMoreCredits: boolean;
  isRunning: boolean;
  manager: AgentManager | null;
  resourceId: string;
  sendMessage: (message: string) => Promise<void>;
  sendProgrammaticMessage: (message: string, options?: ProgrammaticMessageOptions) => Promise<void>;
  sendToolResponse: (toolCallId: string, payload: ToolResponsePayload) => Promise<void>;
  setCurrentThreadId: (threadId: string) => void;
  threadError: Error | null;
  threads: CanvasThreadListItem[];
  workspaceId: string;
  creditAnalyticsParams: CreditAnalyticsParams | null;
  onShowPlansModal?: () => void;
};

const AgentContext = createContext<AgentContextValue | null>(null);

/**
 * Builds a media context entry from CanvasTokenNode stores for all [media:id]
 * references found in the message. When a sourceNode is provided (e.g. from
 * a transform toolbar), it is included directly so the server receives the
 * full node data for the transform target.
 *
 * Returns null if no references are found.
 */
function buildMediaContext(message: string, options?: ProgrammaticMessageOptions): Context | null {
  const mediaMapNodes = getMediaMapNodes(useChatStore.getState());
  const merged = {
    ...mediaMapNodes,
    ...mergeCanvasTokenNodes(
      useCanvasTokenNodeStore.getState().nodes,
      useChatHistoryNodeStore.getState().nodes,
      useChatComposerNodeStore.getState().nodes,
    ),
  };

  const referencedNodes: Record<string, (typeof merged)[string]> = {};

  for (const match of message.matchAll(MEDIA_PATTERN)) {
    const id = match[1];
    const node = merged[id];

    if (node) {
      referencedNodes[id] = node;
    } else {
      // id may be a groupId — include all member nodes so the agent receives the full group
      const members = findGroupMembers(merged, id);
      for (const [memberKey, memberNode] of members) {
        referencedNodes[memberKey] = memberNode;
      }
    }
  }

  // Include the source node (transform target) if provided directly from the callsite.
  // Key by sourceNodeId (the local id embedded in the message as [media:id]) so the AI
  // can match the context to the reference in the prompt.
  if (options?.sourceNode) {
    const key = options.sourceNodeId ?? options.sourceNode?.group?.id;

    if (key) {
      referencedNodes[key] = options.sourceNode;
    }
  }

  if (Object.keys(referencedNodes).length === 0) {
    return null;
  }

  return { description: 'media', value: JSON.stringify(referencedNodes) };
}

export const AgentProvider = memo(
  ({
    agentUrl,
    canConsumeMoreCredits,
    children,
    firstName,
    getAuthToken,
    resourceId,
    resolveResourceId,
    workspaceId,
    creditAnalyticsParams,
    onShowPlansModal,
  }: AgentProviderProps) => {
    const { trackAgentChatPromptSent } = useTrackAgentChatPromptSent();
    const { trackSkillInvoked } = useTrackSkillInvoked();

    const {
      createThread,
      currentThreadId,
      isCurrentThreadHydrated,
      isLoadingThreads,
      setCurrentThreadId,
      threadError,
      threads,
    } = useThreadManagement({
      resolveResourceId,
      resourceId,
      workspaceId,
    });
    const managerRef = useRef<AgentManager | null>(null);
    const busyErrorTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

    // Ref for imperative access to currentThreadId in callbacks
    const currentThreadIdRef = useRef(currentThreadId);
    currentThreadIdRef.current = currentThreadId;

    // Keep a ref to the latest getAuthToken callback so the stable wrapper
    // always calls the most recent version without triggering manager recreation.
    const getAuthTokenRef = useRef(getAuthToken);
    getAuthTokenRef.current = getAuthToken;

    // Create AgentManager — only recreated when agentUrl or workspaceId changes.
    // resourceId updates are applied imperatively via setResourceId to avoid
    // destroying in-flight agent runs during draft→real canvas commit.
    // biome-ignore lint/correctness/useExhaustiveDependencies(resourceId): updated imperatively via manager.setResourceId
    const manager = useMemo(() => {
      managerRef.current?.dispose();

      const newManager = new AgentManager({
        agentUrl,
        getAuthToken: (options) => getAuthTokenRef.current(options),
        getThreadData: (threadId) => useChatStore.getState().threads[threadId] ?? null,
        onMessagesChanged: (threadId, messages) => {
          useChatStore.getState().setMessagesForThread(threadId, messages);
        },
        onStateChanged: (threadId, newState) => {
          useChatStore.getState().setStateForThread(threadId, newState);
        },
        resourceId,
        workspaceId,
      });

      managerRef.current = newManager;
      return newManager;
    }, [agentUrl, workspaceId]);

    // Keep the manager's resourceId in sync without recreating it
    useEffect(() => {
      if (manager && resourceId) {
        manager.setResourceId(resourceId);
      }
    }, [manager, resourceId]);

    useEffect(() => {
      return () => {
        managerRef.current?.dispose();
        managerRef.current = null;
      };
    }, []);

    useEffect(() => {
      return () => {
        busyErrorTimersRef.current.forEach(clearTimeout);
      };
    }, []);

    // Wait for store hydration (S3 messages loaded) before creating the agent,
    // so it initializes with the correct message history rather than an empty array.
    useEffect(() => {
      if (manager && currentThreadId && isCurrentThreadHydrated) {
        manager.getAgent(currentThreadId);
      }
    }, [manager, currentThreadId, isCurrentThreadHydrated]);

    // Clear any stale run error when switching threads so it doesn't reappear on return.
    useEffect(() => {
      if (currentThreadId) {
        useRunErrorStore.getState().clearRunError(currentThreadId);
      }
    }, [currentThreadId]);

    // Guard against concurrent thread creation (e.g. rapid programmatic sends)
    const creatingThreadRef = useRef<Promise<{ threadId: string; resolvedResourceId: string }> | null>(null);
    const ensureThreadId = useCallback(async (): Promise<{ threadId: string; resolvedResourceId: string }> => {
      const existing = currentThreadIdRef.current;

      if (existing) {
        return { resolvedResourceId: resourceId, threadId: existing };
      }

      // createThread() is the guarded version from useThreadManagement — it
      // reuses an existing blank thread rather than creating duplicates.
      if (!creatingThreadRef.current) {
        creatingThreadRef.current = createThread().then(({ thread, resolvedResourceId }) => {
          // Thread id is already set by `guardedCreateThread` → `persistAndSetThread` (without clearing skills).
          manager?.getAgent(thread.id);
          return { resolvedResourceId, threadId: thread.id };
        });
      }

      try {
        return await creatingThreadRef.current;
      } finally {
        creatingThreadRef.current = null;
      }
    }, [createThread, manager, resourceId]);

    const isRunning = useSyncExternalStore(
      useCallback(
        (onStoreChange) => {
          if (!manager) {
            return () => {};
          }

          return manager.subscribeToRunningState(onStoreChange);
        },
        [manager],
      ),
      useCallback(() => {
        if (!manager || !currentThreadId) {
          return false;
        }

        return manager.isRunning(currentThreadId);
      }, [manager, currentThreadId]),
    );

    const sendMessage = useCallback(
      async (message: string) => {
        if (!manager) {
          return;
        }

        const { threadId, resolvedResourceId } = await ensureThreadId();

        trackAgentChatPromptSent({
          canvas_id: resolvedResourceId,
          message_length: message.length,
          thread_id: threadId,
        });

        const agUiMessage: UserMessage = {
          content: message,
          id: `msg_${uuid()}`,
          role: 'user',
        };

        const { selectedModel, isAuto } = getModelSelection(resolvedResourceId);
        const mediaContext = buildMediaContext(message);
        const composerStore = useChatComposerStore.getState();
        const pendingSkills = composerStore.pendingSkillInstructions;
        const commandsInMessage = extractSlashCommandsFromMessage(message);
        for (const skill of pendingSkills) {
          if (commandsInMessage.has(skill.slashCommand)) {
            trackSkillInvoked({
              canvas_id: resolvedResourceId,
              is_invoked_from_onboarding: skill.isInvokedFromOnboarding ?? false,
              skill_id: skill.id,
              slash_command: skill.slashCommand,
            });
          }
        }
        const skillInstructionsValue = selectSkillInstructionsForMessage(pendingSkills, message);

        const context: Context[] = [
          ...(selectedModel && !isAuto ? [{ description: 'modelConfig', value: JSON.stringify(selectedModel) }] : []),
          ...(mediaContext ? [mediaContext] : []),
          ...(skillInstructionsValue ? [{ description: 'skillInstructions', value: skillInstructionsValue }] : []),
        ];

        try {
          await manager.sendMessage({
            context: context.length > 0 ? context : undefined,
            messages: [agUiMessage],
            threadId,
          });
          useChatComposerStore.getState().clearPendingSkillInstructions();
        } catch (error) {
          if (error instanceof AgentBusyError) {
            useChatComposerStore.getState().messageText?.(message);
            useRunErrorStore.getState().setRunError(threadId, {
              code: 'AGENT_BUSY',
              message: error.message,
            });
            // Cancel any existing auto-dismiss timer for this thread so a second busy error
            // doesn't get cleared prematurely by the first timer.
            const BUSY_ERROR_AUTO_DISMISS_MS = 120_000;
            clearTimeout(busyErrorTimersRef.current.get(threadId));
            busyErrorTimersRef.current.set(
              threadId,
              setTimeout(() => {
                useRunErrorStore.getState().clearRunError(threadId);
                busyErrorTimersRef.current.delete(threadId);
              }, BUSY_ERROR_AUTO_DISMISS_MS),
            );
            return;
          }
          // Clear on non-busy errors so stale instructions don't attach to a future retry.
          useChatComposerStore.getState().clearPendingSkillInstructions();
          throw error;
        }
      },
      [ensureThreadId, manager, trackAgentChatPromptSent, trackSkillInvoked],
    );

    const sendProgrammaticMessage = useCallback(
      async (message: string, options?: ProgrammaticMessageOptions) => {
        if (!manager) {
          return;
        }

        const { threadId, resolvedResourceId } = await ensureThreadId();

        const agUiMessage: SystemMessage = {
          content: message,
          id: `msg_${uuid()}`,
          role: 'system',
        };

        const { selectedModel, isAuto } = getModelSelection(resolvedResourceId);
        // A per-call modelOverride wins over the canvas-wide selection so transforms can lock
        // the model regardless of what the user (or auto-selection) has chosen.
        const effectiveModel = options?.modelOverride ?? (isAuto ? null : selectedModel);
        const mediaContext = buildMediaContext(message, options);
        const context: Context[] = [
          { description: 'programmatic', value: 'true' },
          ...(options?.actionName ? [{ description: 'programmaticAction', value: options.actionName }] : []),
          ...(effectiveModel ? [{ description: 'modelConfig', value: JSON.stringify(effectiveModel) }] : []),
          ...(mediaContext ? [mediaContext] : []),
        ];

        await manager.sendMessage({
          context: context.length > 0 ? context : undefined,
          messages: [agUiMessage],
          programmaticOptions: options,
          threadId,
        });
      },
      [ensureThreadId, manager],
    );

    const sendToolResponse = useCallback(
      async (toolCallId: string, payload: ToolResponsePayload) => {
        if (!manager) {
          return;
        }

        const { threadId } = await ensureThreadId();

        const toolMessage: ToolMessage = {
          content: JSON.stringify(payload),
          id: `msg_${uuid()}`,
          role: 'tool',
          toolCallId,
        };

        await manager.sendMessage({
          messages: [toolMessage],
          threadId,
        });
      },
      [manager, ensureThreadId],
    );

    const value = useMemo<AgentContextValue>(
      () => ({
        canConsumeMoreCredits,
        creditAnalyticsParams,
        currentThreadId,
        eventSubscriptions: manager?.eventSubscriptions ?? null,
        firstName,
        isLoadingThreads,
        isRunning,
        manager,
        onShowPlansModal,
        resourceId,
        sendMessage,
        sendProgrammaticMessage,
        sendToolResponse,
        setCurrentThreadId,
        threadError,
        threads,
        workspaceId,
      }),
      [
        currentThreadId,
        firstName,
        isLoadingThreads,
        canConsumeMoreCredits,
        isRunning,
        manager,
        resourceId,
        sendMessage,
        sendProgrammaticMessage,
        setCurrentThreadId,
        threadError,
        threads,
        workspaceId,
        sendToolResponse,
        creditAnalyticsParams,
        onShowPlansModal,
      ],
    );

    return (
      <AgentContext.Provider value={value}>
        <ChatHistoryNodeSync />
        {children}
      </AgentContext.Provider>
    );
  },
);

AgentProvider.displayName = 'AgentProvider';

export const useAgentManager = () => {
  const context = useContext(AgentContext);

  if (!context) {
    throw new Error('useAgentManager must be used within an AgentProvider');
  }

  return context;
};
